import { signal, effect, untracked } from '@preact/signals-core';
import { showToast } from './utils/toast';
import './components/autocomplete';
import './components/data_table';
import './components/asset-map';
import {
  accessTokenSignal,
  authLoadingSignal,
  userProfileSignal,
  setSession,
  clearSession,
  refreshAccessToken,
  setupFetchInterceptor,
  type UserRole
} from './store/auth';
import {
  renderAssetsPage,
  setupAssetsPageListeners,
  initAssetsSignals,
  activeRoleSignal,
  activeCustomerSignal,
  customersSignal,
  refreshAssets,
  registrationSelectedCustomerIdSignal,
  getAuthHeaders,
  generateToken,
  assetsListSignal,
  explorerLevelSignal,
  selectedExplorerCustomerIdSignal,
  selectedExplorerDepartmentIdSignal,
  activeFiltersSignal,
  refreshExplorerDepartments
} from './pages/assets';
import {
  renderAssetAdminPage,
  setupAssetAdminPageListeners,
  refreshAssetAdminData
} from './pages/asset-admin';
import {
  renderCustomersPage,
  setupCustomersPageListeners,
  unmountCustomersPage,
  refreshCustomers
} from './pages/customers';
import {
  renderLocationsPage,
  setupLocationsPageListeners,
  refreshSites,
  refreshDepartments
} from './pages/locations';
import {
  renderDeviceModelsPage,
  setupDeviceModelsPageListeners,
  unmountDeviceModelsPage,
  refreshDeviceModels,
  refreshConsumables
} from './pages/device_models';
import {
  renderConsumablesPage,
  setupConsumablesPageListeners,
  unmountConsumablesPage,
  refreshConsumablesPage
} from './pages/consumables';
import {
  renderLoginPage,
  setupLoginPageListeners
} from './pages/login';
import {
  renderUsersPage,
  setupUsersPageListeners,
  refreshUsers
} from './pages/users';
import {
  renderAuditPage,
  setupAuditPageListeners,
  refreshAuditLogs
} from './pages/audit';
import {
  renderContractsPage,
  setupContractsPageListeners,
  refreshContracts
} from './pages/contracts';
import {
  renderBillingPage,
  setupBillingPageListeners,
  refreshMemos,
  initBillingSignals
} from './pages/billing';
import {
  renderAnalyticsPage,
  setupAnalyticsPageListeners,
  initAnalyticsSignals,
  runPLReport
} from './pages/analytics';
import {
  renderDeviceAnalyticsPage,
  setupDeviceAnalyticsPageListeners,
  unmountDeviceAnalyticsPage,
  refreshDeviceAnalytics,
  initDeviceAnalyticsSignals
} from './pages/device-analytics';
import {
  renderDashboardPage,
  setupDashboardPageListeners,
  unmountDashboardPage,
  refreshDashboard,
  fetchPendingWorkData
} from './pages/dashboard';
import {
  renderTicketsPage,
  setupTicketsPageListeners,
  refreshTickets,
  unmountTicketsPage
} from './pages/tickets';
import {
  renderTicketConsolePage,
  setupTicketConsolePageListeners,
  refreshTicketConsole,
  unmountTicketConsolePage,
  hasUnsavedConsoleWork,
  promptTicketConsoleLeave
} from './pages/ticket-console';
import {
  renderPartsApprovalsDashboard,
  setupPartsApprovalsDashboardListeners,
  unmountPartsApprovalsDashboard,
  refreshPartsApprovalsDashboard
} from './pages/tickets/PartsApprovalsDashboard';
import {
  renderCustomerMergePanel,
  setupCustomerMergePanelListeners
} from './pages/tickets/CustomerMergePanel';
import {
  renderManagerSessionReportsPage,
  setupManagerSessionReportsPageListeners,
  unmountManagerSessionReportsPage,
  refreshSessionReport,
  initSessionReportSignals
} from './pages/manager-session-reports';
import {
  renderNonMpsDetailPage,
  setupNonMpsDetailListeners
} from './pages/non-mps-device-detail';
import {
  renderMachineHistoryPage,
  setupMachineHistoryPageListeners,
  refreshMachineHistory,
  initMachineHistorySignals,
  unmountMachineHistoryPage
} from './pages/machine-history';
import {
  renderTicketSuggestionsPage,
  setupTicketSuggestionsPageListeners,
  refreshTicketSuggestionsPage,
} from './pages/ticket-suggestions';
import { registerServiceWorker, subscribeUserToPush, unsubscribeUserFromPush, getPushSubscriptionStatus, stopPushSubscriptionMaintenanceLoop } from './utils/pwa';

// State definition using @preact/signals-core
export const currentRoute = signal<string>(window.location.hash || '#dashboard');
export const devices = signal<Array<{ id: string; serialNumber: string; status: string }>>([]);
const isDevEnvironment = import.meta.env.DEV;

// Tracks the current page's teardown hook — called before each route swap
let _activePageUnmount: (() => void) | null = null;
let _restoringTicketConsoleRoute = false;
let _quickLookupEffectDisposer: (() => void) | null = null;

// Fetch devices list from backend database
export async function refreshDevices() {
  try {
    const res = await fetch('/api/v1/devices', { headers: getAuthHeaders() });
    if (res.ok) {
      devices.value = await res.json();
    } else {
      devices.value = [];
    }
  } catch (e) {
    console.error('Failed to fetch devices', e);
  }
}

export async function refreshActivePageRoute(route: string) {
  if (route.startsWith('#non-mps-device-detail')) {
    const urlParams = new URLSearchParams(route.split('?')[1] || '');
    const assetId = urlParams.get('id') || '';
    window.location.hash = `#machine-history?id=${encodeURIComponent(assetId)}${urlParams.get('customerId') ? `&customerId=${encodeURIComponent(urlParams.get('customerId') || '')}` : ''}${urlParams.get('deptId') ? `&deptId=${encodeURIComponent(urlParams.get('deptId') || '')}` : ''}`;
  } else if (route.startsWith('#machine-history')) {
    const urlParams = new URLSearchParams(route.split('?')[1] || '');
    const assetId = urlParams.get('id') || '';
    initMachineHistorySignals();
    if (assetId) await refreshMachineHistory(assetId);
  } else if (route.startsWith('#device-analytics')) {
    const urlParams = new URLSearchParams(route.split('?')[1] || '');
    const assetId = urlParams.get('id') || '';
    initDeviceAnalyticsSignals();
    if (assetId) {
      await refreshDeviceAnalytics(assetId);
    }
  } else if (route === '#devices') {
    await refreshDevices();
  } else if (route.startsWith('#assets')) {
    const urlParams = new URLSearchParams(route.split('?')[1] || '');
    const level = urlParams.get('level') || 'customers';
    const custId = urlParams.get('customerId') || null;
    const deptId = urlParams.get('deptId') || null;

    explorerLevelSignal.value = level as any;
    selectedExplorerCustomerIdSignal.value = custId;
    selectedExplorerDepartmentIdSignal.value = deptId;

    if (custId) {
      await refreshExplorerDepartments(custId);
      activeFiltersSignal.value = { ...activeFiltersSignal.value, customerId: custId, siteId: 'all' };
    } else {
      activeFiltersSignal.value = { ...activeFiltersSignal.value, customerId: 'all', siteId: 'all' };
    }
    await refreshAssets();
  } else if (route === '#asset-admin') {
    await refreshAssetAdminData();
  } else if (route === '#customers') {
    await refreshCustomers();
  } else if (route === '#locations') {
    await refreshSites();
    await refreshDepartments();
  } else if (route === '#device-models') {
    await refreshDeviceModels();
    await refreshConsumables();
  } else if (route === '#consumables') {
    await refreshConsumablesPage();
    await refreshDeviceModels();
  } else if (route === '#users') {
    await refreshUsers();
  } else if (route === '#audit') {
    await refreshAuditLogs();
  } else if (route === '#contracts') {
    await refreshContracts();
  } else if (route === '#billing') {
    await refreshMemos();
  } else if (route === '#analytics') {
    await runPLReport();
  } else if (route.startsWith('#tickets')) {
    await refreshTickets();
  } else if (route.startsWith('#ticket-suggestions')) {
    await refreshTicketSuggestionsPage();
  } else if (route.startsWith('#ticket-console')) {
    const urlParams = new URLSearchParams(route.split('?')[1] || '');
    const ticketId = urlParams.get('id') || '';
    if (ticketId) {
      await refreshTicketConsole(ticketId);
    }
  } else if (route === '#parts-approvals') {
    await refreshPartsApprovalsDashboard();
  } else if (route === '#session-reports') {
    initSessionReportSignals();
    await refreshSessionReport();
  } else if (route === '#customer-merge') {
    // No background load necessary
  } else if (route === '#dashboard' || route === '' || !route) {
    await refreshDashboard();
  }
}

// Listen to hash changes for routing and refresh devices dynamically
window.addEventListener('hashchange', () => {
  const hash = window.location.hash || '#dashboard';
  const previousRoute = currentRoute.value || '#dashboard';

  if (_restoringTicketConsoleRoute) {
    _restoringTicketConsoleRoute = false;
    currentRoute.value = hash;
    return;
  }

  if (previousRoute.startsWith('#ticket-console') && hash !== previousRoute && hasUnsavedConsoleWork()) {
    promptTicketConsoleLeave(hash);
    _restoringTicketConsoleRoute = true;
    window.location.hash = previousRoute;
    return;
  }

  currentRoute.value = hash;
  refreshActivePageRoute(hash);
});

// Helper functions for functional page rendering

function renderDevices(): string {
  const deviceListHtml = devices.value.map(d => {
    let badgeTone = 'info';
    if (d.status === 'online') badgeTone = 'success';
    if (d.status === 'error') badgeTone = 'critical';

    return `
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 0; border-bottom: 1px solid var(--p-color-border-subdued);">
        <span><strong>${d.serialNumber}</strong></span>
        <s-badge tone="${badgeTone}">${d.status}</s-badge>
      </div>
    `;
  }).join('');

  return `
    <s-page heading="Printers & Devices" inlineSize="large">
      <s-section>
        <s-box padding="base" background="surface" border="base" borderRadius="base">
          <s-stack gap="base">
            <s-heading element="h3">Registered Assets</s-heading>
            <s-stack gap="small">
              ${deviceListHtml}
            </s-stack>
          </s-stack>
        </s-box>
      </s-section>
    </s-page>
  `;
}

// renderTickets() removed — now uses renderTicketsPage() from ./pages/tickets

function renderSettings(): string {
  return `
    <s-page heading="Settings" inlineSize="large">
      <s-section>
        <s-box padding="base" background="surface" border="base" borderRadius="base">
          <s-stack gap="base">
            <s-heading element="h2">System Settings</s-heading>
            <s-paragraph>Configure TG Managed Printing System parameters, auth settings, and polling frequencies.</s-paragraph>
            
            <s-divider></s-divider>
            
            <s-stack gap="base">
              <s-heading element="h3">Notification Preferences</s-heading>
              <s-paragraph>Manage how you receive alerts and updates for tickets, session events, and parts approvals on this browser.</s-paragraph>
              
              <div id="settings-pwa-banner"></div>
              
              <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 0.5rem; padding: 0.5rem 0;">
                <s-stack gap="none">
                  <s-text style="font-weight: 600;">Browser Push Notifications</s-text>
                  <s-text tone="subdued">Receive real-time alerts about assigned or reopened tickets and parts approvals on this browser.</s-text>
                </s-stack>
                <div id="notification-toggle-container">
                  <s-button id="btn-toggle-notifications" disabled="true">Checking status...</s-button>
                </div>
              </div>
            </s-stack>
          </s-stack>
        </s-box>
      </s-section>
    </s-page>
  `;
}

function renderSupport(): string {
  return `
    <s-page heading="Help & Support" inlineSize="large">
      <s-section>
        <s-box padding="base" background="surface" border="base" borderRadius="base">
          <s-stack gap="base">
            <s-heading element="h2">Contact Support</s-heading>
            <s-paragraph>For assistance with operational issues, contract pricing updates, or database rollback workflows, contact operations support.</s-paragraph>
          </s-stack>
        </s-box>
      </s-section>
    </s-page>
  `;
}

function updateDevSession() {
  const role = activeRoleSignal.value;
  const customerId = activeCustomerSignal.value;
  let sites: string[] = [];

  if (customerId === '00000000-0000-0000-0000-000000000001') {
    sites = ['00000000-0000-0000-0000-000000000011', '00000000-0000-0000-0000-000000000012'];
  } else if (customerId === '00000000-0000-0000-0000-000000000002') {
    sites = ['00000000-0000-0000-0000-000000000021'];
  }

  const token = generateToken(role, customerId, sites);
  setSession(token);
}

// --- Helpers extracted from the effect for reuse ---

/** Maps the current hash route to page HTML. */
function renderPageContent(route: string): string {
  if (route === '#devices') return renderDevices();
  if (route.startsWith('#tickets')) return renderTicketsPage();
  if (route.startsWith('#ticket-suggestions')) return renderTicketSuggestionsPage();
  if (route.startsWith('#ticket-console')) return renderTicketConsolePage();
  if (route === '#parts-approvals') return renderPartsApprovalsDashboard();
  if (route === '#session-reports') return renderManagerSessionReportsPage();
  if (route === '#customer-merge') return renderCustomerMergePanel();
  if (route.startsWith('#assets')) return renderAssetsPage();
  if (route === '#asset-admin') return renderAssetAdminPage();
  if (route === '#customers') return renderCustomersPage();
  if (route === '#locations') return renderLocationsPage();
  if (route === '#device-models') return renderDeviceModelsPage();
  if (route === '#consumables') return renderConsumablesPage();
  if (route === '#users') return renderUsersPage();
  if (route === '#audit') return renderAuditPage();
  if (route === '#contracts') return renderContractsPage();
  if (route === '#billing') return renderBillingPage();
  if (route === '#analytics') return renderAnalyticsPage();
  if (route.startsWith('#machine-history')) return renderMachineHistoryPage();
  if (route.startsWith('#non-mps-device-detail')) return renderNonMpsDetailPage();
  if (route.startsWith('#device-analytics')) return renderDeviceAnalyticsPage();
  if (route === '#settings') return renderSettings();
  if (route === '#support') return renderSupport();
  return renderDashboardPage();
}

/** Binds page-specific event listeners after content is swapped. */
function setupPageListeners(route: string): void {
  if (route.startsWith('#assets')) setupAssetsPageListeners();
  else if (route === '#asset-admin') setupAssetAdminPageListeners();
  else if (route === '#customers') setupCustomersPageListeners();
  else if (route === '#locations') setupLocationsPageListeners();
  else if (route === '#device-models') setupDeviceModelsPageListeners();
  else if (route === '#consumables') setupConsumablesPageListeners();
  else if (route === '#users') setupUsersPageListeners();
  else if (route === '#audit') setupAuditPageListeners();
  else if (route === '#contracts') setupContractsPageListeners();
  else if (route === '#billing') setupBillingPageListeners();
  else if (route === '#analytics') setupAnalyticsPageListeners();
  else if (route.startsWith('#machine-history')) setupMachineHistoryPageListeners();
  else if (route.startsWith('#non-mps-device-detail')) setupNonMpsDetailListeners();
  else if (route.startsWith('#device-analytics')) setupDeviceAnalyticsPageListeners();
  else if (route.startsWith('#tickets')) setupTicketsPageListeners();
  else if (route.startsWith('#ticket-suggestions')) setupTicketSuggestionsPageListeners();
  else if (route.startsWith('#ticket-console')) setupTicketConsolePageListeners();
  else if (route === '#parts-approvals') setupPartsApprovalsDashboardListeners();
  else if (route === '#session-reports') setupManagerSessionReportsPageListeners();
  else if (route === '#customer-merge') setupCustomerMergePanelListeners();
  else if (route === '#settings') setupSettingsPageListeners();
  else if (route === '#dashboard' || route === '' || !route) setupDashboardPageListeners();
}

async function setupSettingsPageListeners() {
  const container = document.getElementById('notification-toggle-container');
  const bannerContainer = document.getElementById('settings-pwa-banner');
  if (!container) return;

  const status = await getPushSubscriptionStatus();

  const updateToggleUI = (currentStatus: 'supported' | 'unsupported' | 'subscribed' | 'unsubscribed' | 'denied') => {
    if (currentStatus === 'unsupported') {
      container.innerHTML = `<s-button disabled="true">Unsupported</s-button>`;
      if (bannerContainer) {
        bannerContainer.innerHTML = `
          <s-banner tone="warning" title="Push notifications are not supported on this browser.">
            Please make sure you are using a modern browser and the application is served over HTTPS.
          </s-banner>
        `;
      }
      return;
    }

    if (currentStatus === 'denied') {
      container.innerHTML = `<s-button id="btn-toggle-notifications" tone="critical">Enable Notifications</s-button>`;
      if (bannerContainer) {
        bannerContainer.innerHTML = `
          <s-banner tone="warning" title="Push notifications are blocked in this browser.">
            Allow notifications in your browser site settings, then try again here.
          </s-banner>
        `;
      }
    } else if (currentStatus === 'subscribed') {
      container.innerHTML = `<s-button id="btn-toggle-notifications" tone="critical">Disable Notifications</s-button>`;
      if (bannerContainer) bannerContainer.innerHTML = '';
    } else {
      container.innerHTML = `<s-button id="btn-toggle-notifications" primary="true">Enable Notifications</s-button>`;
      if (bannerContainer) bannerContainer.innerHTML = '';
    }

    const btn = document.getElementById('btn-toggle-notifications');
    if (btn) {
      btn.onclick = async () => {
        btn.setAttribute('disabled', 'true');
        btn.textContent = currentStatus === 'subscribed' ? 'Disabling...' : 'Enabling...';

        try {
          if (currentStatus === 'subscribed') {
            const disabled = await unsubscribeUserFromPush();
            if (disabled) {
              showToast('Push notifications disabled successfully.', 'success');
              updateToggleUI('unsubscribed');
            } else {
              showToast('Failed to disable push notifications.', 'error');
              updateToggleUI(currentStatus);
            }
          } else {
            const enabled = await subscribeUserToPush();
            const newStatus = await getPushSubscriptionStatus();
            if (enabled && newStatus === 'subscribed') {
              showToast('Push notifications enabled successfully.', 'success');
            } else if (newStatus === 'denied') {
              showToast('Notifications are blocked in this browser.', 'warning');
            } else {
              showToast('Failed to enable push notifications. Check browser permissions.', 'error');
            }
            updateToggleUI(newStatus);
          }
        } catch (e) {
          console.error(e);
          showToast('Error configuring push notifications.', 'error');
          updateToggleUI(currentStatus);
        }
      };
    }
  };

  updateToggleUI(status);
}

/** Returns true if the route requires at least manager-level access. */
function isManagerRoute(route: string): boolean {
  return route === '#users' || route === '#audit' || route === '#contracts' ||
    route === '#billing' || route === '#analytics' ||
    route.startsWith('#device-analytics') || route === '#parts-approvals' || route === '#session-reports';
}

/** Generates a nav link anchor element string. */
function renderNavLink(hash: string, label: string, iconType: string, route: string): string {
  let isActive = route === hash || 
    (hash === '#tickets' && route.startsWith('#tickets')) ||
    (hash === '#ticket-suggestions' && route.startsWith('#ticket-suggestions')) ||
    (hash === '#assets' && route.startsWith('#assets'));
  if (hash === '#assets' && (route.startsWith('#device-analytics') || route.startsWith('#non-mps-device-detail') || route.startsWith('#machine-history'))) {
    isActive = true;
  }
  const activeAttr = isActive ? 'aria-current="page"' : '';
  return `
    <a
      class="nav-link"
      href="${hash}"
      data-nav-hash="${hash}"
      ${activeAttr}
    >
      <div class="nav-link-content">
        <div class="nav-link-icon">
          <s-icon type="${iconType}" size="base"></s-icon>
        </div>
        <s-text>${label}</s-text>
      </div>
    </a>
  `;
}

/** Updates nav link active states without re-rendering the sidebar. */
function updateNavActiveStates(route: string): void {
  document.querySelectorAll('.nav-link[data-nav-hash]').forEach(link => {
    const hash = link.getAttribute('data-nav-hash') || '';
    let isActive = route === hash ||
      (hash === '#tickets' && route.startsWith('#tickets')) ||
      (hash === '#ticket-suggestions' && route.startsWith('#ticket-suggestions'));
    if (hash === '#assets' && (route.startsWith('#device-analytics') || route.startsWith('#non-mps-device-detail') || route.startsWith('#machine-history'))) {
      isActive = true;
    }
    if (isActive) {
      link.setAttribute('aria-current', 'page');
    } else {
      link.removeAttribute('aria-current');
    }
  });
}

/** Renders the sidebar navigation section links, respecting role visibility. */
function renderSidebarNavLinks(route: string): string {
  const role = activeRoleSignal.value;
  const isManagerPlus = role === 'superadmin' || role === 'admin' || role === 'manager';

  return `
    <div class="nav-links-stack">

          <!-- Manager Sticky Pending Work Component (auto-updated every 30s) -->
          <div id="sidebar-manager-pending-container"></div>

          <s-divider color="base"></s-divider>
      <h2 class="nav-section-title">Operations</h2>
      ${renderNavLink('#dashboard', 'Dashboard', 'home', route)}
      ${renderNavLink('#assets', 'Machines', 'inventory', route)}
      ${renderNavLink('#tickets', 'Support Tickets', 'alert-circle', route)}
      ${role !== 'viewer' ? renderNavLink('#ticket-suggestions', 'Ticket Suggestions', 'alert-circle', route) : ''}
      ${isManagerPlus ? renderNavLink('#parts-approvals', 'Parts Approvals', 'status-active', route) : ''}
      ${isManagerPlus ? renderNavLink('#session-reports', 'Session Reports', 'calendar', route) : ''}
      
      ${isManagerPlus ? `
        <h2 class="nav-section-title" style="margin-top: 0.5rem;">Administration</h2>
        ${renderNavLink('#customers', 'Customer Manager', 'person', route)}
        ${renderNavLink('#asset-admin', 'Asset Manager', 'inventory', route)}
        ${renderNavLink('#locations', 'Locations & Depts', 'location', route)}
        ${renderNavLink('#device-models', 'Device Models', 'print', route)}
        ${renderNavLink('#consumables', 'Consumables', 'replace', route)}
        ${renderNavLink('#contracts', 'Contracts Portal', 'inventory-updated', route)}
        
        <h2 class="nav-section-title" style="margin-top: 0.5rem;">Finance</h2>
        ${renderNavLink('#analytics', 'P&L Analytics', 'chart-donut', route)}
        ${renderNavLink('#billing', 'Internal Memos', 'receipt', route)}
        
        <h2 class="nav-section-title" style="margin-top: 0.5rem;">System</h2>
        ${renderNavLink('#users', 'User Directory', 'person', route)}
        ${renderNavLink('#audit', 'Audit Logs', 'alert-circle', route)}
      ` : ''}
    </div>
  `;
}

/** Renders the developer context panel selects. */
function renderDevContextPanel(): string {
  if (!isDevEnvironment) {
    return '';
  }

  return `
    <div style="padding: 1rem; margin-top: auto; border-top: 1px solid var(--p-color-border-subdued); display: flex; flex-direction: column; gap: 0.75rem; background-color: var(--p-color-bg-surface-secondary);">
      <div style="font-weight: bold; font-size: 0.8rem; text-transform: uppercase; color: var(--p-color-text-subdued);">Developer Context</div>
      
      <div style="display: flex; flex-direction: column; gap: 0.25rem;">
        <s-text style="font-size: 0.75rem; font-weight: 500;">Role Context</s-text>
        <select id="dev-role-switcher" style="padding: 0.25rem; font-size: 0.8rem; border: 1px solid var(--p-color-border-subdued); border-radius: 4px; background: var(--p-color-bg-surface); color: var(--p-color-text);">
          <option value="superadmin" ${activeRoleSignal.value === 'superadmin' ? 'selected' : ''}>Superadmin</option>
          <option value="admin" ${activeRoleSignal.value === 'admin' ? 'selected' : ''}>Admin</option>
          <option value="manager" ${activeRoleSignal.value === 'manager' ? 'selected' : ''}>Manager</option>
          <option value="technician" ${activeRoleSignal.value === 'technician' ? 'selected' : ''}>Technician</option>
          <option value="viewer" ${activeRoleSignal.value === 'viewer' ? 'selected' : ''}>Viewer</option>
        </select>
      </div>

      <div style="display: flex; flex-direction: column; gap: 0.25rem;">
        <s-text style="font-size: 0.75rem; font-weight: 500;">Customer Context</s-text>
        <select id="dev-customer-switcher" style="padding: 0.25rem; font-size: 0.8rem; border: 1px solid var(--p-color-border-subdued); border-radius: 4px; background: var(--p-color-bg-surface); color: var(--p-color-text);">
          ${customersSignal.value.map(c => `
            <option value="${c.id}" ${activeCustomerSignal.value === c.id ? 'selected' : ''}>${c.name}</option>
          `).join('')}
        </select>
      </div>
    </div>
  `;
}

// --- Shell state tracking ---
let _shellRendered = false;
let _lastRenderedRoute = '';

export function forceRebuildShell(): void {
  _shellRendered = false;
}

function syncActiveRoleFromAuthenticatedProfile(): void {
  const profile = userProfileSignal.value;
  if (!profile) return;
  if (activeRoleSignal.value === profile.role) return;

  activeRoleSignal.value = profile.role;
  _shellRendered = false;
}

/** Renders the full app shell with persistent sidebar. Returns the shell HTML string. */
function renderAppShell(route: string, bodyHtml: string): string {
  return `
    <style>
      html,
      body {
        margin: 0;
        padding: 0;
        height: 100vh;
        height: 100dvh;
        width: 100vw;
        overflow: hidden;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      }
      body {
        display: flex;
        background-color: var(--p-color-bg-app, #f6f6f7);
      }
      #app,
      s-app-provider {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100vh;
        height: 100dvh;
        overflow: hidden;
      }
      .app-shell-grid {
        display: grid;
        width: 100%;
        height: 100%;
        min-height: 0;
        grid-template-columns: 260px 1fr;
      }
      
      .sidebar-container {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
        overflow-y: auto;
        background-color: var(--p-color-bg-surface-secondary, #f1f2f4);
        border-right: 1px solid var(--p-color-border-subdued, #e1e3e5);
        transition: transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      }
      
      .sidebar-header {
        padding: 1.25rem 1rem;
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }
      
      .sidebar-logo-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        background-color: var(--p-color-bg-accent, #008060);
        color: white;
        border-radius: var(--p-border-radius-200, 8px);
        font-size: 1.25rem;
        font-weight: bold;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      }
      
      .sidebar-title-group {
        display: flex;
        flex-direction: column;
      }
      
      .sidebar-title {
        font-size: 1rem;
        font-weight: 600;
        color: var(--p-color-text, #202223);
        margin: 0;
        line-height: 1.25;
      }
      
      .sidebar-subtitle {
        font-size: 0.75rem;
        color: var(--p-color-text-subdued, #6d7175);
        margin: 0;
        line-height: 1.2;
      }
      
      .nav-links-stack {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        padding: 0.5rem;
      }
      
      .nav-section-title {
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--p-color-text-subdued, #6d7175);
        padding: 0.75rem 0.5rem 0.25rem 0.75rem;
        margin: 0;
      }
      
      .nav-link {
        display: block !important;
        text-decoration: none !important;
        border-radius: var(--p-border-radius-200, 8px) !important;
        color: var(--p-color-text, #202223) !important;
        transition: background-color 0.15s ease, color 0.15s ease;
        position: relative;
        overflow: hidden;
      }
      
      .nav-link:hover {
        background-color: var(--p-color-bg-action-hover, rgba(0, 0, 0, 0.05)) !important;
      }
      
      .nav-link[aria-current="page"] {
        background-color: var(--p-color-bg-surface-selected, #e4e6eb) !important;
        font-weight: 600;
      }
      
      .nav-link[aria-current="page"]::before {
        content: "";
        position: absolute;
        left: 0;
        top: 6px;
        bottom: 6px;
        width: 3px;
        background-color: var(--p-color-border-accent, #008060);
        border-radius: 0 var(--p-border-radius-100, 4px) var(--p-border-radius-100, 4px) 0;
      }
      
      .nav-link-content {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.5rem 0.75rem;
      }
      
      .nav-link-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--p-color-icon, #5c5f62);
      }
      
      .nav-link[aria-current="page"] .nav-link-icon {
        color: var(--p-color-icon-hover, #202223);
      }
      
      .main-content-container {
        height: 100%;
        min-height: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-sizing: border-box;
      }
      
      .page-content-wrapper {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        width: 100%;
        height: 100%;
        padding-bottom: clamp(4rem, 8vh, 7rem);
        scroll-padding-bottom: clamp(4rem, 8vh, 7rem);
        box-sizing: border-box;
      }

      /* Card Hover Effects for Hierarchical Explorer */
      [id^="explorer-customer-"],
      [id^="explorer-dept-"],
      #explorer-all-customer-assets {
        transition: box-shadow 0.15s ease, transform 0.15s ease;
      }
      [id^="explorer-customer-"]:hover,
      [id^="explorer-dept-"]:hover,
      #explorer-all-customer-assets:hover {
        box-shadow: var(--p-shadow-200, 0 3px 6px rgba(0,0,0,0.1));
        transform: translateY(-1px);
      }

      /* Mobile styling */
      .mobile-top-header {
        display: none;
        align-items: center;
        padding: 0.5rem 1rem;
        background-color: var(--p-color-bg-surface, #ffffff);
        border-bottom: 1px solid var(--p-color-border-subdued, #e1e3e5);
        gap: 0.75rem;
        box-shadow: var(--p-shadow-100, 0 1px 2px rgba(0,0,0,0.05));
      }
      
      .sidebar-backdrop {
        display: none;
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.4);
        backdrop-filter: blur(1.5px);
        z-index: 9998;
      }

      #btn-close-sidebar {
        display: none !important;
      }

      @media (max-width: 768px) {
        /* Allow natural page scroll and pull-to-refresh on mobile */
        html,
        body {
          overflow-y: auto !important;
          height: auto !important;
          overscroll-behavior-y: auto !important;
        }
        #app,
        s-app-provider {
          height: auto !important;
          overflow: visible !important;
        }
        .app-shell-grid {
          grid-template-columns: 1fr;
          height: auto !important;
          min-height: 100vh;
        }
        .main-content-container {
          height: auto !important;
          overflow: visible !important;
        }
        .page-content-wrapper {
          overflow-y: visible !important;
          height: auto !important;
          padding: 0.5rem !important;
        }

        /* Scale down components on mobile screens to save space */
        :root {
          font-size: 14px !important;
        }
        s-heading {
          --p-font-size-heading-md: 0.95rem !important;
          --p-font-size-heading-lg: 1.1rem !important;
        }
        s-text {
          font-size: 0.85rem !important;
        }
        s-card {
          --p-space-card-padding: 0.5rem !important;
        }
        s-box {
          --p-space-box-padding: 0.5rem !important;
        }
        s-stack {
          --p-space-stack-gap: 0.35rem !important;
        }
        
        .sidebar-container {
          position: fixed;
          top: 0;
          bottom: 0;
          left: 0;
          width: 260px;
          z-index: 9999;
          transform: translateX(-100%);
          box-shadow: none;
        }
        
        .sidebar-container.expanded {
          transform: translateX(0);
          box-shadow: var(--p-shadow-400, 0 16px 20px rgba(0,0,0,0.15));
        }
        
        .mobile-top-header {
          display: flex;
          justify-content: space-between;
        }
        
        .sidebar-backdrop.active {
          display: block;
        }

        #btn-close-sidebar {
          display: block !important;
          margin-left: auto;
          background: transparent;
          border: none;
          cursor: pointer;
          color: var(--p-color-text-subdued, #6d7175);
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }
    </style>

    <s-app-provider>
      <!-- Mobile sidebar backdrop overlay -->
      <div class="sidebar-backdrop" id="mobile-sidebar-backdrop"></div>

      <!-- Main Layout Grid -->
      <div class="app-shell-grid">
        <!-- Sidebar navigation -->
        <s-box
          as="nav"
          aria-label="Primary"
          class="sidebar-container"
          padding="none"
        >
          <!-- App Header -->
          <div class="sidebar-header">
            <div class="sidebar-title-group">
              <h1 class="sidebar-title">[OPM]</h1>
            </div>
            <!-- Close button for mobile -->
            <button id="btn-close-sidebar" aria-label="Close menu">
              <s-icon type="cancel" size="base"></s-icon>
            </button>
          </div>

          <!-- Quick Lookup Autocomplete -->
          <div style="padding: 0 0.5rem 0.5rem 0.5rem;">
            <div style="position: relative; min-index: 10005; min-height: 40px;">
              <opm-autocomplete
                id="sidebar-quick-lookup"
                placeholder="Search serial or customer..."
              ></opm-autocomplete>
            </div>
          </div>

          <!-- Navigation Links (updated in-place on role changes) -->
          <div id="sidebar-nav-container">
            ${renderSidebarNavLinks(route)}
          </div>

          <!-- Developer Context Panel -->
          <div id="dev-context-panel-container">
            ${renderDevContextPanel()}
          </div>


          <div class="nav-links-stack">
            <h2 class="nav-section-title">System</h2>
            ${renderNavLink('#settings', 'Settings', 'settings', route)}
            ${renderNavLink('#support', 'Help & Support', 'question-circle', route)}
            <a class="nav-link" id="btn-logout" href="#login" style="cursor: pointer;">
              <div class="nav-link-content">
                <div class="nav-link-icon">
                  <s-icon type="log-out" size="base"></s-icon>
                </div>
                <s-text>Log out</s-text>
              </div>
            </a>
          </div>
        </s-box>

        <!-- Main content area -->
        <s-box as="main" class="main-content-container" padding="none" background="app" style="min-width: 0;">
          <!-- Mobile Top Header -->
          <div class="mobile-top-header">
            <s-button id="btn-toggle-sidebar" variant="plain">
              <s-icon type="menu" size="base"></s-icon>
            </s-button>
            <s-heading element="h2" style="font-size: 1rem; margin: 0; line-height: 1;">OPM Portal</s-heading>
            <div style="width: 28px;"></div> <!-- visual spacer -->
          </div>

          <div class="page-content-wrapper" id="main-content-slot">
            ${bodyHtml}
          </div>
          <div style="height: 50px;"></div>
        </s-box>
      </div>
    </s-app-provider>
  `;
}

/** Binds persistent shell listeners (sidebar, dev context, logout, mobile). Called once. */
function setupShellListeners(): void {
  // Bind Developer Switchers (only in dev environment)
  if (isDevEnvironment) {
    // Developer Role Switcher
    const devRoleSelect = document.getElementById('dev-role-switcher') as HTMLSelectElement;
    if (devRoleSelect) {
      devRoleSelect.addEventListener('change', (e) => {
        const val = (e.currentTarget as HTMLSelectElement).value;
        activeRoleSignal.value = val as UserRole;
        updateDevSession();
        // Rebuild nav links to reflect role-based visibility changes
        const navContainer = document.getElementById('sidebar-nav-container');
        if (navContainer) {
          navContainer.innerHTML = renderSidebarNavLinks(currentRoute.value);
          // Re-attach nav link click handlers for mobile close
          bindNavLinkMobileClose();
        }
        refreshActivePageRoute(currentRoute.value);
      });
    }

    // Developer Customer Switcher
    const devCustSelect = document.getElementById('dev-customer-switcher') as HTMLSelectElement;
    if (devCustSelect) {
      devCustSelect.addEventListener('change', (e) => {
        const val = (e.currentTarget as HTMLSelectElement).value;
        activeCustomerSignal.value = val;
        registrationSelectedCustomerIdSignal.value = val;
        updateDevSession();
        refreshActivePageRoute(currentRoute.value);
      });
    }
  }

  // Sync quick lookup items reactively with memory leak protection
  if (_quickLookupEffectDisposer) {
    _quickLookupEffectDisposer();
    _quickLookupEffectDisposer = null;
  }

  _quickLookupEffectDisposer = effect(() => {
    const quickLookup = document.getElementById('sidebar-quick-lookup') as any;
    if (!quickLookup) return;

    const customers = customersSignal.value || [];
    const assets = assetsListSignal.value || [];

    const items = [
      ...customers.map((c: any) => ({
        id: `cust_${c.id}`,
        label: `Customer: ${c.name}`
      })),
      ...assets.map((a: any) => ({
        id: `asset_${a.id}`,
        label: `Serial: ${a.serialNumber}${a.customerName ? ` (${a.customerName})` : ''}`
      }))
    ];

    quickLookup.items = items;
  });

  // Logout
  const logoutBtn = document.getElementById('btn-logout');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      try {
        await fetch('/api/v1/auth/logout', { method: 'POST' });
      } catch (err) {
        console.error('Logout request failed:', err);
      } finally {
        stopPushSubscriptionMaintenanceLoop();
        if (_quickLookupEffectDisposer) {
          _quickLookupEffectDisposer();
          _quickLookupEffectDisposer = null;
        }
        clearSession();
        _shellRendered = false;
        window.location.hash = '#login';
      }
    });
  }

  // Mobile Sidebar Toggle/Close
  const toggleSidebarBtn = document.getElementById('btn-toggle-sidebar');
  const closeSidebarBtn = document.getElementById('btn-close-sidebar');
  const sidebarBackdrop = document.getElementById('mobile-sidebar-backdrop');
  const sidebarMenu = document.querySelector('.sidebar-container');

  const closeMobileSidebar = () => {
    if (sidebarMenu) sidebarMenu.classList.remove('expanded');
    if (sidebarBackdrop) sidebarBackdrop.classList.remove('active');
  };

  const openMobileSidebar = () => {
    if (sidebarMenu) sidebarMenu.classList.add('expanded');
    if (sidebarBackdrop) sidebarBackdrop.classList.add('active');
  };

  if (toggleSidebarBtn) toggleSidebarBtn.addEventListener('click', openMobileSidebar);
  if (closeSidebarBtn) closeSidebarBtn.addEventListener('click', closeMobileSidebar);
  if (sidebarBackdrop) sidebarBackdrop.addEventListener('click', closeMobileSidebar);

  // Auto-close sidebar on nav link click (mobile)
  bindNavLinkMobileClose();

  // Autocomplete Quick Lookup (Customer & Serial number)
  const quickLookup = document.getElementById('sidebar-quick-lookup') as any;
  if (quickLookup) {
    quickLookup.addEventListener('select', (e: CustomEvent) => {
      const selected = e.detail;
      if (!selected || !selected.id) return;

      const id = selected.id as string;
      if (id.startsWith('cust_')) {
        const custId = id.replace('cust_', '');
        window.location.hash = `#assets?customerId=${custId}`;
      } else if (id.startsWith('asset_')) {
        const assetId = id.replace('asset_', '');
        const foundAsset = (assetsListSignal.value || []).find((a: any) => a.id === assetId);
        if (foundAsset) {
          window.location.hash = `#machine-history?id=${encodeURIComponent(foundAsset.id)}`;
        }
      }

      setTimeout(() => {
        quickLookup.clear();
      }, 100);
    });
  }

  // Initialize 30-second background polling timer for manager pending work component
  setupManagerPendingWorkTimer();
}

export const sidebarPendingDismissedSignal = signal<boolean>(sessionStorage.getItem('sidebar_pending_dismissed') === 'true');

let _managerPendingWorkTimer: any = null;

export async function refreshManagerPendingWorkSidebar() {
  const container = document.getElementById('sidebar-manager-pending-container');
  if (!container) return;

  const role = activeRoleSignal.peek();
  const isManager = role === 'superadmin' || role === 'admin' || role === 'manager';

  if (!isManager || sidebarPendingDismissedSignal.peek()) {
    container.style.display = 'none';
    container.innerHTML = '';
    return;
  }

  const pendingData = await fetchPendingWorkData();
  const totalCount = pendingData.pendingSuggestionsCount + pendingData.pendingPartsCount + pendingData.resolvedTicketsCount;

  if (totalCount === 0) {
    container.style.display = 'none';
    container.innerHTML = '';
    return;
  }

  container.style.display = 'block';
  container.innerHTML = `
    <style>
      .opm-sidebar-pending-card {
        margin: 0.5rem;
        padding: 0.65rem 0.75rem;
        background: #ffffff;
        border: 1px solid #e1e3e5;
        border-radius: 8px;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
        position: sticky;
        bottom: 0;
        z-index: 100;
        transition: all 0.2s ease;
      }
      .opm-sidebar-pending-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 0.4rem;
        padding-bottom: 0.35rem;
        border-bottom: 1px solid #f1f2f4;
      }
      .opm-sidebar-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: #5c5f62;
        display: inline-block;
      }
      .opm-sidebar-pending-item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 0.8rem;
        font-weight: 500;
        color: #303133;
        text-decoration: none;
        padding: 0.3rem 0.4rem;
        border-radius: 4px;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .opm-sidebar-pending-item:hover {
        background: #f4f6f8;
        color: #1a1a1a;
      }
      .opm-sidebar-dismiss-btn {
        background: none;
        border: none;
        cursor: pointer;
        font-size: 1rem;
        line-height: 1;
        color: #8c9196;
        padding: 0 2px;
        transition: color 0.15s ease;
      }
      .opm-sidebar-dismiss-btn:hover {
        color: #303133;
      }
    </style>
    <div class="opm-sidebar-pending-card">
      <div class="opm-sidebar-pending-header">
        <div style="display:flex;align-items:center;gap:0.35rem;">
          <span class="opm-sidebar-dot"></span>
          <span style="font-size:0.6875rem;font-weight:700;text-transform:uppercase;letter-spacing:0.04em;color:#616569;">Action Required</span>
        </div>
        <button id="btn-dismiss-sidebar-pending" class="opm-sidebar-dismiss-btn" title="Dismiss">×</button>
      </div>
      <div style="display:flex;flex-direction:column;gap:0.15rem;">
        ${pendingData.pendingSuggestionsCount > 0 ? `
          <a href="#ticket-suggestions" class="opm-sidebar-pending-item">
            <span>Ticket Suggestions</span>
            <s-badge tone="critical">${pendingData.pendingSuggestionsCount}</s-badge>
          </a>
        ` : ''}
        ${pendingData.pendingPartsCount > 0 ? `
          <a href="#parts-approvals" class="opm-sidebar-pending-item">
            <span>Parts Approvals</span>
            <s-badge tone="warning">${pendingData.pendingPartsCount}</s-badge>
          </a>
        ` : ''}
        ${pendingData.resolvedTicketsCount > 0 ? `
          <a href="#tickets" class="opm-sidebar-pending-item">
            <span>Resolved Reviews</span>
            <s-badge tone="success">${pendingData.resolvedTicketsCount}</s-badge>
          </a>
        ` : ''}
      </div>
    </div>
  `;

  const dismissBtn = document.getElementById('btn-dismiss-sidebar-pending');
  if (dismissBtn) {
    dismissBtn.addEventListener('click', () => {
      sidebarPendingDismissedSignal.value = true;
      sessionStorage.setItem('sidebar_pending_dismissed', 'true');
      const c = document.getElementById('sidebar-manager-pending-container');
      if (c) {
        c.style.display = 'none';
        c.innerHTML = '';
      }
    });
  }
}

export function setupManagerPendingWorkTimer() {
  if (_managerPendingWorkTimer) {
    clearInterval(_managerPendingWorkTimer);
    _managerPendingWorkTimer = null;
  }

  void refreshManagerPendingWorkSidebar();

  _managerPendingWorkTimer = setInterval(() => {
    if (document.visibilityState === 'visible') {
      void refreshManagerPendingWorkSidebar();
    }
  }, 30000);
}

/** Binds mobile sidebar close to all current nav links. */
function bindNavLinkMobileClose(): void {
  const sidebarMenu = document.querySelector('.sidebar-container');
  const sidebarBackdrop = document.getElementById('mobile-sidebar-backdrop');
  const closeMobileSidebar = () => {
    if (sidebarMenu) sidebarMenu.classList.remove('expanded');
    if (sidebarBackdrop) sidebarBackdrop.classList.remove('active');
  };
  document.querySelectorAll('.nav-link').forEach(link => {
    link.addEventListener('click', closeMobileSidebar);
  });
}

/** Checks route-level access guards. Returns true if the route is allowed. */
function checkRouteGuard(route: string): boolean {
  const role = activeRoleSignal.value;

  if (isManagerRoute(route)) {
    if (role !== 'superadmin' && role !== 'admin' && role !== 'manager') {
      console.warn(`[Route Guard] Unauthorized access attempt to ${route} by role ${role}`);
      setTimeout(() => { window.location.hash = '#dashboard'; }, 0);
      return false;
    }
  }

  if (route.startsWith('#ticket-console')) {
    if (role === 'viewer') {
      console.warn(`[Route Guard] Unauthorized access attempt to ${route} by role ${role}`);
      setTimeout(() => { window.location.hash = '#dashboard'; }, 0);
      return false;
    }
  }

  if (route.startsWith('#ticket-suggestions') && role === 'viewer') {
    console.warn(`[Route Guard] Unauthorized access attempt to ${route} by role ${role}`);
    setTimeout(() => { window.location.hash = '#dashboard'; }, 0);
    return false;
  }

  if (route === '#customer-merge') {
    if (role !== 'superadmin' && role !== 'admin') {
      console.warn(`[Route Guard] Unauthorized access attempt to ${route} by role ${role}`);
      setTimeout(() => { window.location.hash = '#dashboard'; }, 0);
      return false;
    }
  }

  return true;
}

// Global UI reactivity hook
effect(() => {
  const appContainer = document.getElementById('app');
  if (!appContainer) return;

  // --- Loading state: full-page spinner ---
  if (authLoadingSignal.value) {
    _shellRendered = false;
    appContainer.innerHTML = `
      <s-app-provider>
        <div style="display: flex; justify-content: center; align-items: center; height: 100vh; background-color: var(--p-color-bg-app, #f6f6f7);">
          <s-stack gap="base" align="center">
            <s-spinner size="large"></s-spinner>
            <s-text tone="subdued">Loading OPM application...</s-text>
          </s-stack>
        </div>
      </s-app-provider>
    `;
    return;
  }

  const route = currentRoute.value;

  // --- Unauthenticated: show login page (full replacement) ---
  if (!accessTokenSignal.value) {
    _shellRendered = false;
    if (route !== '#login') {
      setTimeout(() => { window.location.hash = '#login'; }, 0);
    }
    appContainer.innerHTML = renderLoginPage();
    setupLoginPageListeners();
    return;
  }

  syncActiveRoleFromAuthenticatedProfile();

  // --- Authenticated but on login route: redirect to dashboard ---
  if (route === '#login') {
    setTimeout(() => { window.location.hash = '#dashboard'; }, 0);
    return;
  }

  // --- Route guards ---
  if (!checkRouteGuard(route)) return;

  // --- Render page content ---
  const bodyHtml = untracked(() => renderPageContent(route));

  // --- Teardown previous page before DOM swap ---
  if (_activePageUnmount) {
    try {
      _activePageUnmount();
    } catch (e) {
      console.error('[Router] Unmount hook threw:', e);
    }
    _activePageUnmount = null;
  }

  if (!_shellRendered) {
    // First authenticated render: build the entire shell
    appContainer.innerHTML = renderAppShell(route, bodyHtml);
    untracked(() => {
      setupShellListeners();
    });
    _shellRendered = true;
  } else {
    // Subsequent route changes: only swap the content slot
    const slot = document.getElementById('main-content-slot');
    if (slot) {
      const isSameRoute = _lastRenderedRoute === route;
      const prevScrollY = window.scrollY;
      const prevScrollX = window.scrollX;

      slot.innerHTML = bodyHtml;

      if (isSameRoute && prevScrollY > 0) {
        window.scrollTo({
          top: prevScrollY,
          left: prevScrollX,
          behavior: 'instant' as ScrollBehavior
        });
      }
    }
    // Update nav link active states without re-rendering the sidebar
    untracked(() => {
      updateNavActiveStates(route);
    });
    _lastRenderedRoute = route;
  }

  // Bind page-specific listeners (always, since page content was swapped)
  untracked(() => {
    setupPageListeners(route);
  });

  // --- Register new page's unmount hook ---
  if (route.startsWith('#tickets')) {
    _activePageUnmount = unmountTicketsPage;
  } else if (route.startsWith('#ticket-console')) {
    _activePageUnmount = unmountTicketConsolePage;
  } else if (route === '#dashboard' || route === '' || !route) {
    _activePageUnmount = unmountDashboardPage;
  } else if (route === '#parts-approvals') {
    _activePageUnmount = unmountPartsApprovalsDashboard;
  } else if (route.startsWith('#machine-history')) {
    _activePageUnmount = unmountMachineHistoryPage;
  } else if (route === '#customers') {
    _activePageUnmount = unmountCustomersPage;
  } else if (route === '#device-models') {
    _activePageUnmount = unmountDeviceModelsPage;
  } else if (route === '#consumables') {
    _activePageUnmount = unmountConsumablesPage;
  } else if (route === '#session-reports') {
    _activePageUnmount = unmountManagerSessionReportsPage;
  } else if (route.startsWith('#device-analytics')) {
    _activePageUnmount = unmountDeviceAnalyticsPage;
  } else {
    _activePageUnmount = null;
  }
});

// Watch for login transitions to initialize data signals
let _isDataInitialized = false;
effect(() => {
  const token = accessTokenSignal.value;
  if (token && !_isDataInitialized) {
    _isDataInitialized = true;
    untracked(() => {
      initAssetsSignals().then(() => {
        initBillingSignals().then(() => {
          initAnalyticsSignals();
          // Force active page route refresh with loaded signals
          refreshActivePageRoute(currentRoute.value);
        });
      });
    });
  } else if (!token) {
    _isDataInitialized = false;
  }
});



// Setup the global fetch interceptor first
setupFetchInterceptor();

// Update checks must run even when an installed app has no active session.
void registerServiceWorker();

// Perform initial silent refresh check on startup
async function initAuth() {
  try {
    await refreshAccessToken();
  } catch (err) {
    console.log('[Auth Init] No active session on startup');
  } finally {
    authLoadingSignal.value = false;
  }
}

initAuth().then(() => {
  refreshActivePageRoute(currentRoute.value);
});
